import React from 'react';
import { Box, CircularProgress, IconButton, Tooltip, alpha, useTheme } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';

import {
  OperationProcessChildRow,
  OperationProcessRow,
  OperationProcessSiteInfo,
} from '../../services/OperationProcessService';

// ===== 카테고리 정의 =====
export const CATEGORIES = [
  { value: 'infra', label: '인프라 구축', tone: 'indigo' },
  { value: 'map', label: '맵 제작', tone: 'green' },
  { value: 'video', label: '영상 처리 서버', tone: 'amber' },
  { value: 'service', label: '서비스 설치', tone: 'pink' },
  { value: 'etc', label: '기타', tone: 'gray' },
] as const;

const TONE_COLORS: Record<string, { bg: string; fg: string }> = {
  indigo: { bg: '#eef2ff', fg: '#4338ca' },
  green: { bg: '#ecfdf5', fg: '#047857' },
  amber: { bg: '#fef3c7', fg: '#92400e' },
  pink: { bg: '#fce7f3', fg: '#9d174d' },
  gray: { bg: '#f3f4f6', fg: '#4b5563' },
};

export function categoryTone(value: string): { bg: string; fg: string } {
  const cat = CATEGORIES.find((c) => c.value === value);
  return TONE_COLORS[cat?.tone ?? 'gray'];
}

// 헤더 입력 필드 정의
export const SITE_INFO_FIELDS: Array<{ key: keyof OperationProcessSiteInfo; label: string }> = [
  { key: 'location', label: '위치' },
  { key: 'manager', label: '담당자' },
  { key: 'startDate', label: '시작일' },
  { key: 'endDate', label: '완료 예정' },
  { key: 'customer', label: '고객사' },
  { key: 'contact', label: '연락처' },
  { key: 'siteCode', label: '현장코드' },
  { key: 'remark', label: '비고' },
];

export const EMPTY_SITE_INFO: OperationProcessSiteInfo = {
  siteName: '',
  location: '',
  manager: '',
  startDate: '',
  endDate: '',
  customer: '',
  contact: '',
  siteCode: '',
  remark: '',
};

/**
 * 행/자식 행의 진행 상태.
 *  - done    : 체크 완료
 *  - current : 위에서부터 첫 미체크 (= 지금 진행해야 할 것)
 *  - pending : 그 외 미체크 (= 대기)
 *
 * view 모드에서 OrderBadge / StatusIcon / 행 강조에 사용한다.
 */
export type RowState = 'done' | 'current' | 'pending';

/** 순서 번호 배지 — 상태별 색상으로 진행 흐름을 한눈에 보여준다. */
interface OrderBadgeProps {
  order: number;
  state: RowState;
}

export const OrderBadge: React.FC<OrderBadgeProps> = ({ order, state }) => {
  const theme = useTheme();
  const palette = {
    done: { bg: alpha(theme.palette.success.main, 0.16), fg: theme.palette.success.main },
    current: { bg: theme.palette.primary.main, fg: '#fff' },
    pending: {
      bg: alpha(theme.palette.text.primary, 0.06),
      fg: theme.palette.text.secondary,
    },
  }[state];
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 26,
        height: 22,
        px: 0.75,
        borderRadius: 999,
        fontSize: '0.72rem',
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
        bgcolor: palette.bg,
        color: palette.fg,
        boxShadow:
          state === 'current'
            ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}`
            : 'none',
      }}
    >
      {order}
    </Box>
  );
};

/** 상태 아이콘 — view 모드에서 체크박스 대신 노출. */
export const StatusIcon: React.FC<{ state: RowState }> = ({ state }) => {
  const theme = useTheme();
  if (state === 'done') {
    return <CheckCircleIcon fontSize="small" sx={{ color: theme.palette.success.main }} />;
  }
  if (state === 'current') {
    return <PlayCircleFilledIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />;
  }
  return (
    <RadioButtonUncheckedIcon
      fontSize="small"
      sx={{ color: theme.palette.text.disabled }}
    />
  );
};

interface StatusToggleProps {
  state: RowState;
  /** 현재 완료 여부 — 클릭 시 반대로 토글한다. */
  checked: boolean;
  /** 이 단계가 저장 중일 때 스피너 노출. */
  busy?: boolean;
  /** 다른 단계 저장 중 등으로 클릭을 막을 때. */
  disabled?: boolean;
  onToggle: () => void;
  testId?: string;
}

/**
 * view 모드 상태 토글 — StatusIcon 을 그대로 보여주되 아이콘을 클릭하면
 * 완료/해지가 토글된다. 별도의 '완료'/'취소' 버튼 없이 한 번의 클릭으로
 * 처리해 상호작용을 단순화한다(시각은 기존 상태 아이콘 그대로 유지).
 */
export const StatusToggle: React.FC<StatusToggleProps> = ({
  state,
  checked,
  busy = false,
  disabled = false,
  onToggle,
  testId,
}) => {
  const label = checked ? '클릭하여 완료 해지' : '클릭하여 완료 처리';
  return (
    <Tooltip title={label} arrow>
      <span>
        <IconButton
          size="small"
          onClick={onToggle}
          disabled={disabled}
          aria-label={label}
          data-testid={testId}
          sx={{
            p: 0.5,
            borderRadius: 1.5,
            transition: 'background-color 120ms ease, transform 120ms ease',
            '&:hover': { bgcolor: 'action.hover', transform: 'scale(1.15)' },
            '&:active': { transform: 'scale(0.95)' },
          }}
        >
          {busy ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            <StatusIcon state={state} />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export function makeRow(category: string = 'infra'): OperationProcessRow {
  return {
    id: `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    category,
    checked: false,
    name: '',
    date: '',
    content: '',
    docLinks: [],
    worker: '',
    remark: '',
    children: [],
  };
}

export function makeChild(): OperationProcessChildRow {
  return {
    id: `child-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    checked: false,
    name: '',
    content: '',
    date: '',
    remark: '',
    worker: '',
  };
}
